/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { api } from "@/lib/api";
import useSEO from "@/hooks/useSEO";

export default function ForgotPassword() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  useSEO({
    title: "Reset your Lumioos password",
    description: "Forgot your Lumioos password? Enter your email and we'll send a secure reset link. Get back to your AI workspace in seconds.",
    canonical: "https://mylumioos.com/forgot-password",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="forgot-password-page">
      <div className="flex flex-col px-6 sm:px-12 py-8 max-w-md mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex"><Logo size="md" /></Link>
          <LangToggle variant="outline" />
        </div>

        <div className="mt-16">
          {!sent ? (
            <>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                {t("fp_eyebrow")}
              </div>
              <h1 className="mt-2 text-4xl font-black tracking-tighter">{t("fp_title")}</h1>
              <p className="mt-3 text-zinc-600">{t("fp_subtitle")}</p>

              <form onSubmit={submit} className="mt-10 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                    {t("auth_email")}
                  </label>
                  <input
                    data-testid="fp-email-input"
                    required type="email" autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full px-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-md"
                  />
                </div>
                <button
                  type="submit" data-testid="fp-submit-btn" disabled={loading}
                  className="btn-accent w-full inline-flex items-center justify-center gap-2 font-semibold py-3.5 rounded-md disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>{t("fp_send_btn")} <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="fp-sent-confirmation">
              <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-emerald-100">
                <MailCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="mt-6 text-3xl font-black tracking-tighter">{t("fp_sent_title")}</h1>
              <p className="mt-3 text-zinc-600">
                {t("fp_sent_body", { email })}
              </p>
              <p className="mt-2 text-sm text-zinc-500">{t("fp_sent_hint")}</p>
            </div>
          )}

          <div className="mt-8">
            <Link to="/auth" className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-600 hover:text-blue-600" data-testid="fp-back-link">
              <ArrowLeft className="w-4 h-4" /> {t("fp_back_login")}
            </Link>
          </div>

          {/* SEO copy — gives this page meaningful body content for crawlers
              (previously had ~30 words, flagged in the audit). */}
          <section className="mt-16 text-sm text-zinc-600 leading-7 space-y-4">
            <h2 className="text-base font-black text-zinc-900">How the password reset works</h2>
            <p>
              Enter the email address you use to sign in to Lumioos and we'll send a single-use reset link
              that's valid for one hour. Click it on the same device to set a new password — we recommend at
              least 12 characters with a mix of letters, numbers and a symbol. The link can only be used
              once, so if you accidentally close the tab, just request another.
            </p>
            <h2 className="text-base font-black text-zinc-900">Didn't get the email?</h2>
            <p>
              Reset emails arrive within 30 seconds. If yours doesn't, check your spam folder for a message
              from <code>no-reply@mylumioos.com</code> and mark it "not spam" so the next one lands in your
              inbox. If you signed up with Google, you don't have a password — go back to <Link to="/auth" className="font-semibold text-blue-600 hover:underline">sign in</Link> and use the Google button instead.
            </p>
            <h2 className="text-base font-black text-zinc-900">Account security tips</h2>
            <p>
              Never reuse the same password across services. Lumioos hashes passwords with bcrypt and only
              ever sees the plaintext during sign-in. If you suspect your account is compromised, reset the
              password here and email <Link to="/support" className="font-semibold text-blue-600 hover:underline">support</Link> to invalidate every active session immediately.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
